import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import axios from '@/api/axios'
import router from '@/router'
import { useAppStore } from './app'

function extractTokenFromURL() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('token')
}

function storeTokenInSessionStorage(token: string) {
  window.sessionStorage.setItem('user', token)
}

function removeTokenFromURL() {
  const url = new URL(window.location.href)
  url.searchParams.delete('token')
  history.replaceState(null, '', url.toString())
}

type State = {
  name: User['fullname'] | null
  role: User['role'] | null
  uid: User['_id'] | null
  userDetails: User | null
  isLoggedIn: boolean
  checked: boolean
}

let lastRedirect = ''

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    name: null,
    role: null,
    uid: null,
    userDetails: null,
    isLoggedIn: false,
    checked: false,
  }),

  getters: {
    credits: state => {
      const { userDetails } = state
      if (!userDetails) return 0

      const creditsExpire = userDetails.institution?.creditsExpire
      if (creditsExpire && dayjs().isAfter(dayjs(creditsExpire))) {
        return 0
      }

      return userDetails.institution?.credits ?? userDetails.credits ?? 0
    },
    aiCredits: state => {
      const { userDetails } = state
      if (!userDetails) return 0
      return userDetails.institution?.aiCredits ?? userDetails.aiCredits ?? 0
    },
    isAIEnabled: () => {
      return import.meta.env.VITE_HIDE_AI !== 'true'
    },
    disableRemotesGames: state =>
      state.userDetails?.institution
        ? Boolean(state.userDetails.institution.disableRemotesGames)
        : Boolean(state.userDetails?.disableRemotesGames),
    getUserDetails: state => state.userDetails,
    user: state => state.userDetails,
    institutionDetails: state => state.userDetails?.institution,
    getInstitutionName: state => state.userDetails?.institution?.name,
    getUserName: state => state.name,
    isAuth: state => state.isLoggedIn,
    getUid: state => state.uid,
    getRole: state => state.role,
    r0: state => state.role === 0,
    r1: state => state.role === 1,
    r2: state => state.role === 2,
    isAdmin: state => state.role !== null && state.role < 2,
    isInstitution: state => !!state.userDetails?.institution,
  },

  actions: {
    login(user: User) {
      this.isLoggedIn = true
      this.userDetails = user
      this.name = user.fullname
      this.role = user.role
      this.uid = user._id
    },

    clearData() {
      this.isLoggedIn = false
      this.name = null
      this.role = null
      this.uid = null
    },

    async checkLogin() {
      try {
        const token = extractTokenFromURL()
        if (token) {
          storeTokenInSessionStorage(token)
          removeTokenFromURL()
        }
        const { data } = await axios.post('/auth/checkLogin', {
          lang: this.locale,
        })
        this.login(data)
      } catch (e: any) {
        const isRedirect = e.response?.data?.redirect
        console.error(e)
        if (isRedirect && isRedirect !== lastRedirect) {
          lastRedirect = isRedirect
          return isRedirect
        }
      } finally {
        this.checked = true
      }
    },

    async logout() {
      try {
        useAppStore().setLoadingActive(true)
        this.clearData()

        await axios.post('/auth/logout')
        await router.push({ name: 'Login', params: { lang: this.locale } })
      } catch (e: any) {
        console.error(e)
      }
    },
  },
})
